import {
  ChangeDetectorRef,
  Directive,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output
} from '@angular/core';
import { Subscription } from 'rxjs';
import { PaginationService } from './pagination.service';

interface PageLink {
  label: string | number;
  value: number;
}

@Directive({
  standalone: true,
  selector: 'pagination-template,[pagination-template]',
  exportAs: 'paginationApi'
})
export class PaginationControlsDirective implements OnInit, OnChanges, OnDestroy {
  @Input() id?: string;
  @Input() maxSize = 7;
  @Output() pageChange = new EventEmitter<number>();
  @Output() pageBoundsCorrection = new EventEmitter<number>();

  pages: PageLink[] = [];
  private changeSub: Subscription;

  constructor(private service: PaginationService, private changeDetectorRef: ChangeDetectorRef) {
    this.changeSub = this.service.change.subscribe(changedId => {
      if (this.id === changedId) {
        this.updatePageLinks();
        this.changeDetectorRef.markForCheck();
      }
    });
  }

  ngOnInit(): void {
    if (this.id === undefined) {
      this.id = this.service.defaultId();
    }
    this.updatePageLinks();
  }

  ngOnChanges(): void {
    this.updatePageLinks();
  }

  ngOnDestroy(): void {
    this.changeSub.unsubscribe();
  }

  previous(): void {
    this.setCurrent(this.getCurrent() - 1);
  }

  next(): void {
    this.setCurrent(this.getCurrent() + 1);
  }

  isFirstPage(): boolean {
    return this.getCurrent() === 1;
  }

  isLastPage(): boolean {
    return this.getLastPage() === this.getCurrent();
  }

  setCurrent(page: number): void {
    this.pageChange.emit(page);
  }

  getCurrent(): number {
    return this.service.getCurrentPage(this.id!);
  }

  getLastPage(): number {
    const inst = this.service.getInstance(this.id!);
    if (inst.totalItems! < 1) {
      return 1;
    }
    return Math.ceil(inst.totalItems! / inst.itemsPerPage);
  }

  private updatePageLinks(): void {
    const inst = this.service.getInstance(this.id!);
    const correctedCurrentPage = this.outOfBoundCorrection(inst);
    if (correctedCurrentPage !== inst.currentPage) {
      setTimeout(() => {
        this.pageBoundsCorrection.emit(correctedCurrentPage);
        this.pages = this.createPageArray(inst.currentPage, inst.itemsPerPage, inst.totalItems!, this.maxSize);
      });
    } else {
      this.pages = this.createPageArray(inst.currentPage, inst.itemsPerPage, inst.totalItems!, this.maxSize);
    }
  }

  private outOfBoundCorrection(instance: { currentPage: number; itemsPerPage: number; totalItems?: number }): number {
    const totalPages = Math.ceil(instance.totalItems! / instance.itemsPerPage);
    if (totalPages < instance.currentPage && totalPages > 0) {
      return totalPages;
    }
    if (instance.currentPage < 1) {
      return 1;
    }
    return instance.currentPage;
  }

  private createPageArray(
    currentPage: number,
    itemsPerPage: number,
    totalItems: number,
    paginationRange: number
  ): PageLink[] {
    paginationRange = +paginationRange;
    const pages: PageLink[] = [];
    const totalPages = Math.ceil(totalItems / itemsPerPage);
    const halfWay = Math.ceil(paginationRange / 2);
    const isStart = currentPage <= halfWay;
    const isEnd = totalPages - halfWay < currentPage;
    const isMiddle = !isStart && !isEnd;
    const ellipsesNeeded = paginationRange < totalPages;

    let i = 1;
    while (i <= totalPages && i <= paginationRange) {
      const pageNumber = this.calculatePageNumber(i, currentPage, paginationRange, totalPages);
      const openingEllipsesNeeded = i === 2 && (isMiddle || isEnd);
      const closingEllipsesNeeded = i === paginationRange - 1 && (isMiddle || isStart);
      const label = ellipsesNeeded && (openingEllipsesNeeded || closingEllipsesNeeded) ? '...' : pageNumber;
      pages.push({ label, value: pageNumber });
      i++;
    }
    return pages;
  }

  private calculatePageNumber(i: number, currentPage: number, paginationRange: number, totalPages: number): number {
    const halfWay = Math.ceil(paginationRange / 2);
    if (i === paginationRange) {
      return totalPages;
    }
    if (i === 1) {
      return i;
    }
    if (paginationRange < totalPages) {
      if (totalPages - halfWay < currentPage) {
        return totalPages - paginationRange + i;
      }
      if (halfWay < currentPage) {
        return currentPage - halfWay + i;
      }
      return i;
    }
    return i;
  }
}
